import { Typography, LinkListHeading, Box, GridContainer, Row, Column, Divider, AspectRatio, FlexBox, FluidImage, tokens, useScreenClass } from '@vp/swan'
import { useEffect, useState } from 'react'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { Article } from '../../types/article.type'
import { LastModifiedDate } from './LastModifiedDate'
import './ArticleContent.scss'
import { HelpCenterSearch } from '@vp/help-center-articles-search'
import { useIdentity } from '@vp/ubik-context'
import { FeedbackComponent } from '@vp/feedback-ui'
import { t } from 'i18next'
import { HelpCenterUrls } from '../../types/helpCenterUrls.type'
import { useArticleVote } from '../../hooks/useArticleVote'
import { ArticleToc } from '../article-toc/ArticleToc'
import { ArticleItem } from '@vp/help-center-knowledge-anatomies'
import { usePeopleAlsoViewed } from '../../hooks/usePeopleAlsoViewed'
import { injectHeadingIdsIntoDOM } from '../../utils/heading.utils'
import { parseArticleBody } from '../../utils/articleParser.utils'
import Faqs from '../faq/Faqs'

interface ArticleContentProps {
  article: Article
  tenant?: string
  helpCenterUrls?: HelpCenterUrls | null
  locale: string
  useStaging?: boolean
}

export const ArticleContent = ({ article, tenant = 'vistaprint', helpCenterUrls, locale, useStaging }: ArticleContentProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [photoIndex, setPhotoIndex] = useState(0)
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [showDivider, setShowDivider] = useState(false)
  const [deviceType, setDeviceType] = useState('md')
  const [showPeopleAlsoViewed, setShowPeopleAlsoViewed] = useState(true)
  const [parsedArticleBody, setParsedArticleBody] = useState<React.ReactElement[]>([])
  const identity = useIdentity()
  const { data } = useArticleVote(article.sfId, locale)
  const lng = locale.toLowerCase().replace('-', '_')

  const { articleNumbers: peopleAlsoViewedNumbers } = usePeopleAlsoViewed({
    peopleAlsoViewedHtml: article.peopleAlsoViewed,
    locale,
    tenant,
    useStaging,
  })
  const screenSize = useScreenClass()

  useEffect(() => {
    const parsed = parseArticleBody(article.articleBody)
    setParsedArticleBody(parsed)
  }, [article.articleBody])

  const handleOnClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const { tagName } = e.target as HTMLElement
    if (tagName === 'IMG') {
      const { src } = e.target as HTMLImageElement
      const index = imageUrls.indexOf(src)
      if (index >= 0) {
        setPhotoIndex(index)
        setIsOpen(true)
      }
    }
  }
  useEffect(() => {
    const parser = new DOMParser()
    const doc = parser.parseFromString(article.articleBody, 'text/html')
    const images = Array.from(doc.getElementsByTagName('img')) as HTMLImageElement[]

    if (images.length) {
      setImageUrls(images.map((img) => img.src))
    }
  }, [article])

  const handleRender = (isShown: boolean) => {
    setShowDivider(isShown)
  }

  // Inject IDs into h2 elements in the DOM after rendering
  useEffect(() => {
    injectHeadingIdsIntoDOM('.article-content')
  }, [parsedArticleBody])

  const handleArticleError = (hasError: boolean) => {
    if (hasError) {
      setShowPeopleAlsoViewed(false)
    }
  }

  const getRelatedArticleNumbers = (count: number): string[] => {
    if (!article.relatedArticles || article.relatedArticles.length === 0) return []

    return article.relatedArticles
      .slice(0, count)
      .map((relatedArticle) => relatedArticle.articleId)
      .filter(Boolean)
  }

  const getArticlesToShow = (): string[] => {
    const peopleAlsoViewedCount = peopleAlsoViewedNumbers.length

    if (peopleAlsoViewedCount >= 2) {
      return peopleAlsoViewedNumbers.slice(0, 2)
    } else if (peopleAlsoViewedCount === 1) {
      const relatedArticles = getRelatedArticleNumbers(1)
      return [...peopleAlsoViewedNumbers, ...relatedArticles]
    } else {
      return getRelatedArticleNumbers(2)
    }
  }

  const articlesToShow = getArticlesToShow()

  useEffect(() => {
    const updateArticleWidth = () => {
      const minScreenWidthXs = 768
      const screenWidthMd = 1024
      if (window && window.innerWidth >= minScreenWidthXs && window.innerWidth < screenWidthMd) {
        // if screen is sm
        setDeviceType('sm')
      } else if (window && window.innerWidth >= screenWidthMd) {
        // if screen is md or lg
        setDeviceType('md')
      } else {
        setDeviceType('xs')
      }
    }
    updateArticleWidth()
    window.addEventListener('resize', updateArticleWidth)
    return () => window.removeEventListener('resize', updateArticleWidth)
  }, [])

  const ArticleVideo = ({ videoUrl, videoCaption }: { videoUrl: string; videoCaption?: string }) => {
    const getEmbedUrl = (url: string) => {
      const baseUrl = url.replace('youtube.com/watch?v=', 'youtube.com/embed/')
      const languageCode = locale.split('-')[0].toLowerCase()
      return `${baseUrl}?cc_load_policy=1&cc_lang_pref=${languageCode}`
    }
    const embedUrl = getEmbedUrl(videoUrl)
    return (
      <AspectRatio ratio={0.56}>
        <iframe
          style={{
            position: 'absolute',
            width: '100%',
            height: '100%',
            border: 'none',
          }}
          src={embedUrl}
          title={videoCaption || ''}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
        />
      </AspectRatio>
    )
  }

  return (
    <>
      <GridContainer>
        <Row>
          <Column span={1} display={{ xs: 'none', sm: 'none', md: 'block', lg: 'block' }} />
          <Column span={deviceType === 'xs' ? 2 : deviceType === 'sm' ? 8 : 7} className="article-title">
            <Typography fontSkin="title-headline">{article.articleTitle}</Typography>
            <LastModifiedDate lastModifiedDate={article.lastModifiedDate} locale={article.locale} tenant={tenant} />
          </Column>
          <Column span={1} display={{ xs: 'none', sm: 'block', md: 'block', lg: 'block' }} />
          <Column span={3} display={{ xs: 'none', sm: 'block', md: 'block', lg: 'block' }} />
        </Row>
      </GridContainer>
      <GridContainer>
        <Row>
          <Column span={1} display={{ xs: 'none', sm: 'none', md: 'block', lg: 'block' }} />
          <Column span={deviceType === 'xs' ? 2 : deviceType === 'sm' ? 8 : 7} className="article-container">
            <>
              {
                // eslint-disable-next-line no-constant-binary-expression
                false && (
                  <Box>
                    <Typography fontSkin="title-section">{article.quickAnswer}</Typography>
                  </Box>
                )
              }
              <Box display={{ xs: 'block', sm: 'none', md: 'none', lg: 'none' }}>
                <Box className="search-box">
                  <LinkListHeading swanStyle={{ pt: 0, pb: 0 }}>
                    {t(`${tenant}:articles.search_title`, { lng: article.locale.toLowerCase().replace('-', '_') }) || 'Still have questions?'}
                  </LinkListHeading>
                  <HelpCenterSearch
                    applicationName={'Help Center Article Fragment'}
                    locale={article.locale}
                    resultsPageUrl={helpCenterUrls?.newPlatformSearchUrl}
                    environment="prod"
                  />
                </Box>
              </Box>

              {article.coverImageUrl && article.coverImageUrl.trim() !== '' && (
                <FlexBox flexDirection="column">
                  <FluidImage
                    src={article.coverImageUrl}
                    alt={article.coverImageCaption ?? ''}
                    style={{
                      borderRadius: '16px',
                      aspectRatio: screenSize === 'xs' ? '16/10' : '21/9',
                    }}
                  />
                  {article.coverImageCaption && (
                    <Typography fontSkin="footnote" fontSize="xsmall" textAlign="right" pt={4}>
                      {article.coverImageCaption}
                    </Typography>
                  )}
                </FlexBox>
              )}
              <Box onClick={handleOnClick} className="content text-size-5 article-content">
                {parsedArticleBody}
                {isOpen && <Lightbox open={isOpen} close={() => setIsOpen(false)} slides={imageUrls.map((src) => ({ src }))} index={photoIndex} />}
              </Box>

              {/* YouTube Video */}
              {article.videoUrl && article.videoUrl.trim() !== '' && (
                <FlexBox flexDirection="column">
                  <ArticleVideo videoUrl={article.videoUrl} videoCaption={article.videoCaption} />
                  {article.videoCaption && (
                    <Typography fontSkin="footnote" fontSize="xsmall" pt={4}>
                      {article.videoCaption}
                    </Typography>
                  )}
                </FlexBox>
              )}

              {/* FAQs */}
              {article.faqs && article.faqs.length > 0 && <Faqs tenant={tenant} locale={locale} faqIds={article.faqs} useStaging={useStaging} />}

              {showDivider && <Divider />}
              <Box>
                <FeedbackComponent
                  locale={locale}
                  onRender={handleRender}
                  origin="HelpCenterArticle"
                  previousVote={data?.upvote ? 'upVote' : data?.upvote === false ? 'downVote' : undefined}
                  trackingData={{ category: 'Help Center:Article Page' }}
                  articleData={{
                    articleId: article.sfId,
                    knowledgeKavId: article.knowledgeKavId,
                    articleTitle: article.articleTitle,
                    articleNumber: article.articleNumber,
                    articleVersion: article.articleVersion,
                  }}
                  auth={identity.auth!}
                />
              </Box>
            </>
          </Column>
          <Column span={1} display={{ xs: 'none', sm: 'block', md: 'block', lg: 'block' }} />
          <Column span={3} display={{ xs: 'none', sm: 'block', md: 'block', lg: 'block' }} style={{ overflow: 'visible' }}>
            <div className="sidebar-section">
              <GridContainer className="search-box">
                <LinkListHeading swanStyle={{ pt: 0, pb: 0 }}>
                  {t(`${tenant}:articles.search_title`, { lng: article.locale.toLowerCase().replace('-', '_') }) || 'Still have questions?'}
                </LinkListHeading>
                <HelpCenterSearch
                  applicationName={'Help Center Article Fragment'}
                  locale={article.locale}
                  resultsPageUrl={helpCenterUrls?.newPlatformSearchUrl}
                  environment="prod"
                />
              </GridContainer>
              <div className="article-toc-wrapper" style={{ position: 'sticky', top: '20px' }}>
                <ArticleToc tenant={tenant} locale={locale} articleBody={article.articleBody} />
              </div>
            </div>
          </Column>
        </Row>
      </GridContainer>
      {articlesToShow.length > 0 && showPeopleAlsoViewed && (
        <GridContainer>
          <Row>
            <Column span={1} display={{ xs: 'none', sm: 'none', md: 'block', lg: 'block' }} />
            <Column span={deviceType === 'xs' ? 2 : deviceType === 'sm' ? 8 : 7}>
              <Divider style={{ marginBottom: tokens.SwanSemSpaceBetweenSubsections }} />
              <FlexBox flexDirection="column">
                <Typography fontSkin="title-subsection" style={{ marginBottom: tokens.SwanSemSpaceToActions }}>
                  {t(`${tenant}:articles.people_also_viewed`, { lng })}
                </Typography>
                <Row>
                  {articlesToShow.slice(0, 2).map((articleNumber, index) => (
                    <Column key={articleNumber} span={6} spanXs={12} spanSm={6} spanMd={6} spanLg={6} mb={{ xs: index === 0 ? 5 : 0 }}>
                      <ArticleItem articleNumber={articleNumber} locale={locale} useStaging={useStaging} onArticleError={handleArticleError} />
                    </Column>
                  ))}
                </Row>
              </FlexBox>
            </Column>
            <Column span={1} display={{ xs: 'none', sm: 'block', md: 'block', lg: 'block' }} />
          </Row>
        </GridContainer>
      )}
    </>
  )
}
